import { z } from 'zod';

export const CalDavAccountStatus = z.enum([
  'idle',
  'discovering',
  'connected',
  'auth_failed',
  'error',
]);
export type CalDavAccountStatus = z.infer<typeof CalDavAccountStatus>;

export const CalDavAuthType = z.enum(['basic', 'google']);
export type CalDavAuthType = z.infer<typeof CalDavAuthType>;

export const CalendarSyncStatus = z.enum(['idle', 'connected', 'error']);
export type CalendarSyncStatus = z.infer<typeof CalendarSyncStatus>;

export const CalendarNotificationMode = z.enum(['off', 'event', 'default']);
export type CalendarNotificationMode = z.infer<
  typeof CalendarNotificationMode
>;

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const calendarTimestamp = z.coerce
  .number()
  .int()
  .nonnegative()
  .max(8_640_000_000_000_000);

export const CalDavAccount = z.object({
  id: z.number().int().positive(),
  authType: CalDavAuthType,
  name: z.string(),
  serverUrl: z.string(),
  username: z.string(),
  hasPassword: z.boolean(),
  allowPrivateNetwork: z.boolean(),
  enabled: z.boolean(),
  status: CalDavAccountStatus,
  statusError: z.string().nullable(),
  principalUrl: z.string().nullable(),
  homeSetUrl: z.string().nullable(),
  calendarCount: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
  lastDiscoveredAt: z.number().int().nonnegative().nullable(),
});
export type CalDavAccount = z.infer<typeof CalDavAccount>;

export const CalDavCalendar = z.object({
  id: z.number().int().positive(),
  kind: z.enum(['caldav', 'ics']),
  caldavAccountId: z.number().int().positive().nullable(),
  href: z.string().nullable(),
  displayName: z.string(),
  color: hexColor,
  readOnly: z.boolean(),
  supportsEvents: z.boolean(),
  supportsTasks: z.boolean(),
  taskSyncEnabled: z.boolean(),
  taskLastSyncedAt: z.number().int().nonnegative().nullable(),
  taskSyncError: z.string().nullable(),
  notificationMode: CalendarNotificationMode,
  defaultTimedReminderMinutes: z.number().int().min(0).max(40_320),
  defaultAllDayReminderMinutes: z.number().int().min(-1_439).max(40_320),
  visible: z.boolean(),
  refreshMinutes: z.number().int().min(0).max(1_440),
  lastSyncedAt: z.number().int().nonnegative().nullable(),
  feedHost: z.string().nullable(),
  feedAllowPrivateNetwork: z.boolean(),
  syncStatus: CalendarSyncStatus,
  syncError: z.string().nullable(),
});
export type CalDavCalendar = z.infer<typeof CalDavCalendar>;

export const CalendarState = z.object({
  accounts: z.array(CalDavAccount),
  calendars: z.array(CalDavCalendar),
});
export type CalendarState = z.infer<typeof CalendarState>;

export const CalendarEventRange = z
  .object({
    start: calendarTimestamp,
    end: calendarTimestamp,
  })
  .refine((value) => value.end > value.start, 'End must be after start')
  .refine(
    (value) => value.end - value.start <= 120 * 24 * 60 * 60 * 1_000,
    'Calendar ranges are limited to 120 days',
  );
export type CalendarEventRange = z.infer<typeof CalendarEventRange>;

const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const CalendarEventOccurrence = z.object({
  id: z.number().int().positive(),
  calendarObjectId: z.number().int().positive(),
  calendarId: z.number().int().positive(),
  calendarName: z.string(),
  color: hexColor,
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
  allDay: z.boolean(),
  startDate: calendarDate.nullable(),
  endDate: calendarDate.nullable(),
  recurrenceId: z.string().nullable(),
  summary: z.string(),
  location: z.string().nullable(),
  status: z.string().nullable(),
  // Planner tasks are projected into the client calendar with synthetic
  // occurrence ids. Provider responses omit both optional markers.
  itemType: z.enum(['event', 'task']).optional(),
  plannerTaskId: z.number().int().positive().optional(),
});
export type CalendarEventOccurrence = z.infer<typeof CalendarEventOccurrence>;

export const CalendarEventAttendee = z.object({
  name: z.string().nullable(),
  address: z.string(),
  participationStatus: z.string().nullable(),
  role: z.string().nullable(),
});
export type CalendarEventAttendee = z.infer<typeof CalendarEventAttendee>;

export const CalendarEventReminderMinutes = z.number().int().min(0).max(40_320);
export type CalendarEventReminderMinutes = z.infer<
  typeof CalendarEventReminderMinutes
>;

const CalendarEventReminders = z
  .array(CalendarEventReminderMinutes)
  .max(5)
  .refine(
    (items) => new Set(items).size === items.length,
    'Reminders must be unique',
  );

export const CalendarEventRsvpStatus = z.enum([
  'accepted',
  'tentative',
  'declined',
]);
export type CalendarEventRsvpStatus = z.infer<
  typeof CalendarEventRsvpStatus
>;

export const CalendarEventInvitee = z
  .object({
    name: z.string().trim().max(300).nullable().default(null),
    address: z
      .email()
      .max(320)
      .transform((value) => value.trim().toLowerCase()),
  })
  .strict();
export type CalendarEventInvitee = z.infer<typeof CalendarEventInvitee>;

const CalendarEventInvitees = z
  .array(CalendarEventInvitee)
  .max(100)
  .refine(
    (items) =>
      new Set(items.map((item) => item.address.toLowerCase())).size ===
      items.length,
    'Guests must have unique email addresses',
  );

export const CalendarEventModificationReason = z.enum([
  'recurring',
  'read_only',
  'account_disabled',
  'unsupported',
  'refresh_required',
]);
export type CalendarEventModificationReason = z.infer<
  typeof CalendarEventModificationReason
>;

export const CalendarEventMutationScope = z.enum(['this', 'following', 'all']);
export type CalendarEventMutationScope = z.infer<typeof CalendarEventMutationScope>;

const CalendarEventCreateDate = calendarDate.refine((value) => {
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return (
    Number.isFinite(timestamp) &&
    timestamp >= 0 &&
    new Date(timestamp).toISOString().slice(0, 10) === value
  );
}, 'Enter a valid calendar date');

export const CalendarRecurrenceWeekday = z.enum(['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']);
export type CalendarRecurrenceWeekday = z.infer<typeof CalendarRecurrenceWeekday>;

export const CalendarEventRecurrence = z
  .object({
    frequency: z.enum(['daily', 'weekly', 'monthly', 'yearly']),
    interval: z.number().int().min(1).max(99).default(1),
    weekdays: z
      .array(CalendarRecurrenceWeekday)
      .max(7)
      .refine((days) => new Set(days).size === days.length, 'Weekdays must be unique')
      .optional(),
    end: z.discriminatedUnion('type', [
      z.object({ type: z.literal('never') }).strict(),
      z.object({ type: z.literal('count'), count: z.number().int().min(2).max(500) }).strict(),
      z.object({ type: z.literal('until'), date: CalendarEventCreateDate }).strict(),
    ]),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.frequency !== 'weekly' && value.weekdays !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['weekdays'],
        message: 'Weekdays are only available for weekly events',
      });
    }
    if (value.weekdays?.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['weekdays'],
        message: 'Choose at least one weekday',
      });
    }
  });
export type CalendarEventRecurrence = z.infer<typeof CalendarEventRecurrence>;

export const CalendarEventDetails = CalendarEventOccurrence.extend({
  description: z.string().nullable(),
  organizer: z
    .object({
      name: z.string().nullable(),
      address: z.string(),
    })
    .nullable(),
  attendees: z.array(CalendarEventAttendee),
  url: z.url().nullable(),
  recurring: z.boolean(),
  recurrenceRule: z.string().nullable(),
  recurrence: CalendarEventRecurrence.nullable(),
  recurrenceStartDate: calendarDate.nullable(),
  calendarNotificationsEnabled: z.boolean(),
  notificationMode: CalendarNotificationMode,
  reminderMinutesBefore: CalendarEventReminders,
  remindersEditable: z.boolean(),
  providerAlarmTimes: z.array(calendarTimestamp).max(20),
  wintermuteAlarmTimes: z.array(calendarTimestamp).max(20),
  selfAttendee: CalendarEventAttendee.nullable(),
  canRespond: z.boolean(),
  lastModified: calendarTimestamp.nullable(),
  revision: z.string().regex(/^[a-f0-9]{64}$/),
  canModify: z.boolean(),
  modificationReason: CalendarEventModificationReason.nullable(),
  mutationScopes: z.array(CalendarEventMutationScope).min(1),
});
export type CalendarEventDetails = z.infer<typeof CalendarEventDetails>;

export const CalendarEventList = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
  events: z.array(CalendarEventOccurrence),
});
export type CalendarEventList = z.infer<typeof CalendarEventList>;

export const CalendarEventSearchQuery = z.object({
  q: z.string().trim().min(2).max(200),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type CalendarEventSearchQuery = z.infer<
  typeof CalendarEventSearchQuery
>;

export const CalendarEventSearchResponse = z.object({
  query: z.string(),
  events: z.array(CalendarEventOccurrence),
  truncated: z.boolean(),
});
export type CalendarEventSearchResponse = z.infer<
  typeof CalendarEventSearchResponse
>;

export const CalendarSyncResult = z.object({
  calendars: z.number().int().nonnegative(),
  objects: z.number().int().nonnegative(),
  occurrences: z.number().int().nonnegative(),
  skippedObjects: z.number().int().nonnegative(),
  failures: z.array(
    z.object({
      calendarId: z.number().int().positive(),
      calendarName: z.string(),
      error: z.string().regex(/^[a-z0-9_]+$/),
    }),
  ),
});
export type CalendarSyncResult = z.infer<typeof CalendarSyncResult>;

export const CalendarTimeZone = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[A-Za-z0-9_+\-/]+$/)
  .refine(isSupportedTimeZone, 'Use a supported IANA time zone');
export type CalendarTimeZone = z.infer<typeof CalendarTimeZone>;

const CalendarAvailabilityCalendarIds = z
  .array(z.number().int().positive())
  .min(1)
  .max(100)
  .refine((ids) => new Set(ids).size === ids.length, 'Calendars must be unique');

export const CalendarAvailabilityRequest = z
  .object({
    calendarIds: CalendarAvailabilityCalendarIds,
    startDate: CalendarEventCreateDate,
    endDate: CalendarEventCreateDate,
    timeZone: CalendarTimeZone,
    weekdays: z
      .array(z.number().int().min(0).max(6))
      .min(1)
      .max(7)
      .refine((days) => new Set(days).size === days.length, 'Weekdays must be unique'),
    workStartMinutes: z.number().int().min(0).max(1_439),
    workEndMinutes: z.number().int().min(1).max(1_440),
    durationMinutes: z.number().int().min(15).max(480),
    bufferBeforeMinutes: z.number().int().min(0).max(240).default(0),
    bufferAfterMinutes: z.number().int().min(0).max(240).default(0),
    slotIntervalMinutes: z.number().int().min(5).max(60).default(15),
    limit: z.number().int().min(1).max(100).default(30),
  })
  .strict()
  .superRefine((value, ctx) => {
    const start = Date.parse(`${value.startDate}T00:00:00Z`);
    const end = Date.parse(`${value.endDate}T00:00:00Z`);
    if (end < start) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'End date cannot be before start date',
      });
    } else if (end - start > 31 * 24 * 60 * 60 * 1_000) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'Availability searches are limited to 32 days',
      });
    }
    if (value.workEndMinutes <= value.workStartMinutes) {
      ctx.addIssue({
        code: 'custom',
        path: ['workEndMinutes'],
        message: 'Working hours must end after they start',
      });
    } else if (
      value.durationMinutes +
        value.bufferBeforeMinutes +
        value.bufferAfterMinutes >
      value.workEndMinutes - value.workStartMinutes
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationMinutes'],
        message: 'Meeting and buffers must fit inside working hours',
      });
    }
  });
export type CalendarAvailabilityRequest = z.infer<
  typeof CalendarAvailabilityRequest
>;

export const CalendarAvailabilityCoverageState = z.enum([
  'complete',
  'partial',
  'missing',
  'error',
]);
export type CalendarAvailabilityCoverageState = z.infer<
  typeof CalendarAvailabilityCoverageState
>;

export const CalendarAvailabilityCoverage = z.object({
  calendarId: z.number().int().positive(),
  calendarName: z.string(),
  color: hexColor,
  state: CalendarAvailabilityCoverageState,
  coveredStart: calendarTimestamp.nullable(),
  coveredEnd: calendarTimestamp.nullable(),
  lastSyncedAt: calendarTimestamp.nullable(),
  error: z.string().nullable(),
});
export type CalendarAvailabilityCoverage = z.infer<
  typeof CalendarAvailabilityCoverage
>;

export const CalendarAvailabilitySlot = z.object({
  start: calendarTimestamp,
  end: calendarTimestamp,
  status: z.enum(['free', 'unknown']),
  unknownCalendarIds: z.array(z.number().int().positive()).max(100),
});
export type CalendarAvailabilitySlot = z.infer<
  typeof CalendarAvailabilitySlot
>;

export const CalendarAvailabilityResponse = z.object({
  start: calendarTimestamp,
  end: calendarTimestamp,
  timeZone: CalendarTimeZone,
  coverage: z.array(CalendarAvailabilityCoverage).max(100),
  slots: z.array(CalendarAvailabilitySlot).max(100),
  busyEventCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type CalendarAvailabilityResponse = z.infer<
  typeof CalendarAvailabilityResponse
>;

const CalendarEventCreateBase = z.object({
  requestId: z.uuid(),
  calendarId: z.number().int().positive(),
  summary: z.string().trim().min(1).max(1_000),
  location: z.string().trim().max(2_000).optional(),
  description: z.string().trim().max(50_000).optional(),
  attendees: CalendarEventInvitees.default([]),
  reminders: CalendarEventReminders.default([]),
  recurrence: CalendarEventRecurrence.nullable().default(null),
});

export const CalendarEventCreate = z
  .discriminatedUnion('allDay', [
    CalendarEventCreateBase.extend({
      allDay: z.literal(false),
      start: calendarTimestamp,
      end: calendarTimestamp,
      timeZone: CalendarTimeZone.default('UTC'),
    }),
    CalendarEventCreateBase.extend({
      allDay: z.literal(true),
      startDate: CalendarEventCreateDate,
      endDate: CalendarEventCreateDate,
    }),
  ])
  .superRefine((value, ctx) => {
    const start = value.allDay ? Date.parse(`${value.startDate}T00:00:00Z`) : value.start;
    const end = value.allDay ? Date.parse(`${value.endDate}T00:00:00Z`) : value.end;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      ctx.addIssue({ code: 'custom', path: ['end'], message: 'End must be after start' });
    } else if (end - start > 366 * 24 * 60 * 60 * 1_000) {
      ctx.addIssue({
        code: 'custom',
        path: ['end'],
        message: 'Events are limited to 366 days',
      });
    }
    if (value.recurrence?.end.type === 'until') {
      const firstDate = value.allDay
        ? value.startDate
        : dateInTimeZone(value.start, value.timeZone);
      if (value.recurrence.end.date < firstDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['recurrence', 'end', 'date'],
          message: 'Recurrence end date cannot be before the first event',
        });
      }
    }
    if (value.recurrence?.frequency === 'weekly' && value.recurrence.weekdays) {
      const firstDate = value.allDay
        ? value.startDate
        : dateInTimeZone(value.start, value.timeZone);
      const firstWeekday = recurrenceWeekday(firstDate);
      if (!value.recurrence.weekdays.includes(firstWeekday)) {
        ctx.addIssue({
          code: 'custom',
          path: ['recurrence', 'weekdays'],
          message: 'Weekly recurrence must include the first event day',
        });
      }
    }
  });
export type CalendarEventCreate = z.infer<typeof CalendarEventCreate>;

export const CalendarEventCreateResponse = z.object({
  calendarObjectId: z.number().int().positive(),
  occurrences: z.number().int().positive(),
  recovered: z.boolean(),
});
export type CalendarEventCreateResponse = z.infer<typeof CalendarEventCreateResponse>;

const CalendarEventUpdateBase = z.object({
  requestId: z.uuid(),
  revision: z.string().regex(/^[a-f0-9]{64}$/),
  targetCalendarId: z.number().int().positive().nullable().default(null),
  scope: CalendarEventMutationScope.default('all'),
  recurrenceId: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}Z?)?$/)
    .max(100)
    .nullable()
    .default(null),
  summary: z.string().trim().min(1).max(1_000),
  location: z.string().trim().max(2_000).nullable(),
  description: z.string().trim().max(50_000).nullable(),
  attendees: CalendarEventInvitees.optional(),
  reminders: CalendarEventReminders.optional(),
  recurrence: CalendarEventRecurrence.optional(),
});

export const CalendarEventUpdate = z
  .discriminatedUnion('allDay', [
    CalendarEventUpdateBase.extend({
      allDay: z.literal(false),
      start: calendarTimestamp,
      end: calendarTimestamp,
    }),
    CalendarEventUpdateBase.extend({
      allDay: z.literal(true),
      startDate: CalendarEventCreateDate,
      endDate: CalendarEventCreateDate,
    }),
  ])
  .superRefine((value, ctx) => {
    const start = value.allDay ? Date.parse(`${value.startDate}T00:00:00Z`) : value.start;
    const end = value.allDay ? Date.parse(`${value.endDate}T00:00:00Z`) : value.end;
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      ctx.addIssue({ code: 'custom', path: ['end'], message: 'End must be after start' });
    } else if (end - start > 366 * 24 * 60 * 60 * 1_000) {
      ctx.addIssue({
        code: 'custom',
        path: ['end'],
        message: 'Events are limited to 366 days',
      });
    }
    if (value.scope !== 'all' && !value.recurrenceId) {
      ctx.addIssue({
        code: 'custom',
        path: ['recurrenceId'],
        message: 'Choose a recurring occurrence to change',
      });
    }
    if (value.recurrence !== undefined && value.scope !== 'all') {
      ctx.addIssue({
        code: 'custom',
        path: ['recurrence'],
        message: 'Recurrence rules can only be changed for the entire series',
      });
    }
  });
export type CalendarEventUpdate = z.infer<typeof CalendarEventUpdate>;

export const CalendarEventUpdateResponse = z.object({
  calendarObjectId: z.number().int().positive(),
  occurrences: z.number().int().nonnegative(),
  recovered: z.boolean(),
});
export type CalendarEventUpdateResponse = z.infer<typeof CalendarEventUpdateResponse>;

export const CalendarEventDelete = z
  .object({
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    scope: CalendarEventMutationScope.default('all'),
    recurrenceId: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}Z?)?$/)
      .max(100)
      .nullable()
      .default(null),
  })
  .superRefine((value, ctx) => {
    if (value.scope !== 'all' && !value.recurrenceId) {
      ctx.addIssue({
        code: 'custom',
        path: ['recurrenceId'],
        message: 'Choose a recurring occurrence to delete',
      });
    }
  });
export type CalendarEventDelete = z.infer<typeof CalendarEventDelete>;

export const CalendarEventDeleteResponse = z.object({
  deleted: z.literal(true),
  alreadyMissing: z.boolean(),
});
export type CalendarEventDeleteResponse = z.infer<typeof CalendarEventDeleteResponse>;

export const CalendarEventRsvp = z
  .object({
    requestId: z.uuid(),
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    status: CalendarEventRsvpStatus,
  })
  .strict();
export type CalendarEventRsvp = z.infer<typeof CalendarEventRsvp>;

export const CalendarEventRsvpResponse = z.object({
  calendarObjectId: z.number().int().positive(),
  status: CalendarEventRsvpStatus,
  recovered: z.boolean(),
});
export type CalendarEventRsvpResponse = z.infer<
  typeof CalendarEventRsvpResponse
>;

export const GoogleCalendarOAuthStatus = z.object({
  configured: z.boolean(),
  callbackUrl: z.url(),
});
export type GoogleCalendarOAuthStatus = z.infer<typeof GoogleCalendarOAuthStatus>;

export const GoogleCalendarOAuthStart = z.object({
  currentPassword: z.string().min(1).max(1_024),
});
export type GoogleCalendarOAuthStart = z.infer<typeof GoogleCalendarOAuthStart>;

export const GoogleCalendarOAuthStartResponse = z.object({
  authorizationUrl: z.url(),
});
export type GoogleCalendarOAuthStartResponse = z.infer<
  typeof GoogleCalendarOAuthStartResponse
>;

const CalDavConnectionFields = z.object({
  name: z.string().trim().min(1).max(100),
  serverUrl: z.url().max(2_048),
  username: z.string().trim().min(1).max(1_024),
  allowPrivateNetwork: z.boolean(),
  enabled: z.boolean(),
});

export const CalDavAccountCreate = CalDavConnectionFields.extend({
  password: z.string().min(1).max(4_096),
  allowPrivateNetwork: z.boolean().default(false),
  enabled: z.boolean().default(true),
  currentPassword: z.string().min(1).max(1_024),
});
export type CalDavAccountCreate = z.infer<typeof CalDavAccountCreate>;

export const CalDavAccountUpdate = CalDavConnectionFields.partial().extend({
  password: z.string().min(1).max(4_096).optional(),
  currentPassword: z.string().min(1).max(1_024),
});
export type CalDavAccountUpdate = z.infer<typeof CalDavAccountUpdate>;

export const CalDavCalendarUpdate = z
  .object({
    visible: z.boolean().optional(),
    color: hexColor.optional(),
    taskSyncEnabled: z.boolean().optional(),
    notificationMode: CalendarNotificationMode.optional(),
    defaultTimedReminderMinutes: z
      .number()
      .int()
      .min(0)
      .max(40_320)
      .optional(),
    defaultAllDayReminderMinutes: z
      .number()
      .int()
      .min(-1_439)
      .max(40_320)
      .optional(),
    refreshMinutes: z
      .number()
      .int()
      .min(0)
      .max(1_440)
      .refine((value) => value === 0 || value >= 5, 'Use 0 or at least 5 minutes')
      .optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'At least one setting is required');
export type CalDavCalendarUpdate = z.infer<typeof CalDavCalendarUpdate>;

const IcsFeedFields = z.object({
  name: z.string().trim().min(1).max(100),
  url: z.url().max(8_192),
  color: hexColor,
  refreshMinutes: z
    .number()
    .int()
    .min(0)
    .max(1_440)
    .refine((value) => value === 0 || value >= 5, 'Use 0 or at least 5 minutes'),
  visible: z.boolean(),
  allowPrivateNetwork: z.boolean(),
});

export const IcsFeedCreate = IcsFeedFields.extend({
  color: hexColor.default('#7c3aed'),
  refreshMinutes: IcsFeedFields.shape.refreshMinutes.default(60),
  visible: z.boolean().default(true),
  allowPrivateNetwork: z.boolean().default(false),
  currentPassword: z.string().min(1).max(1_024),
}).strict();
export type IcsFeedCreate = z.infer<typeof IcsFeedCreate>;

export const IcsFeedUpdate = IcsFeedFields.partial()
  .extend({
    currentPassword: z.string().min(1).max(1_024),
  })
  .strict()
  .refine(
    (value) => Object.keys(value).some((key) => key !== 'currentPassword'),
    'At least one feed setting is required',
  );
export type IcsFeedUpdate = z.infer<typeof IcsFeedUpdate>;

function isSupportedTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

function dateInTimeZone(timestamp: number, timeZone: string): string {
  try {
    const values = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
        .formatToParts(new Date(timestamp))
        .map((part) => [part.type, part.value]),
    );
    return `${values.year}-${values.month}-${values.day}`;
  } catch {
    return new Date(timestamp).toISOString().slice(0, 10);
  }
}

function recurrenceWeekday(date: string): CalendarRecurrenceWeekday {
  const weekdays: CalendarRecurrenceWeekday[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
  return weekdays[new Date(`${date}T00:00:00Z`).getUTCDay()] ?? 'MO';
}
